import dot from 'dot-object';
import {Settings} from '@ui/settings/settings';

/**
 * Returns true only if the given ad slot in settings has visible markup
 * (i.e. anything that would actually paint a host element on the page).
 *
 * Why this exists: AdHost is rendered by SitePageLayout under a simple
 * truthy check (`!!dot.pick('ads.general_top', settings)`). That check is
 * too lax — if an admin saves a slot with whitespace, an HTML comment, or
 * only a <script> tag, the value is "truthy" and AdHost renders an empty
 * wrapper. Because the wrapper carries `min-h-90` + `py-20`, this leaves
 * an 80–180px invisible vertical gap between header and slider on the
 * homepage (mobile + desktop) until a real ad is wired up.
 *
 * Strategy: strip <script> blocks and HTML comments, trim, and require
 * non-empty leftover content. Standard AdSense / GAM blocks always carry
 * an <ins> or <iframe> host element next to their loader script, so this
 * does NOT regress real ads — only the "placeholder/whitespace/comment"
 * case is rejected.
 *
 * Future re-activation: once an ad provider's real markup is pasted into
 * Admin → Appearance → Ads, this helper returns true automatically and
 * AdHost re-renders with no code change required.
 */
export function hasMeaningfulAdSlot(
    settings: Settings,
    dotPath: string,
): boolean {
    const raw = dot.pick(dotPath, settings);
    if (typeof raw !== 'string') return false;
    const cleaned = raw
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .trim();
    return cleaned.length > 0;
}
